(function () {
  'use strict';

  if (window.carismaProductPageLoaded) return;
  window.carismaProductPageLoaded = true;

  document.querySelectorAll('[data-product-page]').forEach(initProductPage);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-product-page]').forEach(initProductPage);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-product-page]').forEach((root) => {
      if (root.carismaCleanup) root.carismaCleanup();
    });
  });

  // Dawn's header cart icon does not refresh automatically when we add to
  // cart via a custom fetch call. This re-renders just the cart-icon-bubble
  // section so the count in the header updates without a full page reload.
  function updateCartIconBubble() {
    fetch('/?sections=cart-icon-bubble')
      .then((res) => res.json())
      .then((sections) => {
        const html = sections['cart-icon-bubble'];

        if (!html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newBubble = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
        const currentBubble = document.querySelector('#cart-icon-bubble');

        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .catch(() => {
        fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            const countEl = document.querySelector(
              '.cart-count-bubble span[aria-hidden="true"], [data-cart-count]'
            );

            if (countEl) {
              countEl.textContent = cart.item_count;
            }
          })
          .catch(() => {});
      });
  }

  // =========================================================
  // MONEY FORMAT
  // =========================================================
  function money(cents, currency) {
    const value = Number(cents) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency', currency, minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  }

  // =========================================================
  // PRODUCT MEDIA GALLERY
  // =========================================================
  function initProductGallery(root, lifecycle) {
    const gallery = root.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const viewport = gallery.querySelector('[data-gallery-viewport]');
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const counter = gallery.querySelector('[data-gallery-counter]');
    if (!viewport || !track || !slides.length) return;
    const on = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: lifecycle.signal });
    let index = 0;
    let pointer = null;

    function pauseSlide(slide) {
      slide.querySelectorAll('video').forEach(video => video.pause());
      // Removing an external player reliably stops sound without loading a provider SDK.
      slide.querySelectorAll('iframe').forEach(frame => frame.remove());
    }

    function loadExternal(slide) {
      const template = slide.querySelector('[data-gallery-external]');
      if (template && !slide.querySelector('iframe') && !document.hidden) slide.append(template.content.cloneNode(true));
    }

    function go(next) {
      index = (next + slides.length) % slides.length;
      gallery.dataset.galleryActive = String(index);
      const activeSlide = slides[index];
      const ratio = Number(activeSlide.dataset.mediaRatio);
      if (Number.isFinite(ratio) && ratio > 0) gallery.style.setProperty('--carisma-gallery-ratio', String(ratio));
      gallery.classList.toggle('carisma-product-gallery--video', ['video', 'external_video'].includes(activeSlide.dataset.mediaType));
      track.style.transform = 'translate3d(-' + index * 100 + '%, 0, 0)';
      slides.forEach((slide, i) => {
        const active = i === index;
        if (!active) pauseSlide(slide);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      loadExternal(slides[index]);
      if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
    }

    on(gallery.querySelector('[data-gallery-previous]'), 'click', () => go(index - 1));
    on(gallery.querySelector('[data-gallery-next]'), 'click', () => go(index + 1));
    on(viewport, 'keydown', event => {
      // Leave the native video player's keyboard controls alone.
      if (event.target !== viewport || slides.length < 2) return;
      const keys = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: slides.length - 1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      go(keys[event.key]);
    });
    on(viewport, 'pointerdown', event => {
      if (slides.length < 2 || !event.isPrimary || event.button !== 0 || event.target.closest('video, iframe, button, a')) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      viewport.setPointerCapture(event.pointerId);
    });
    on(viewport, 'pointerup', event => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const dx = pointer.x - event.clientX;
      const dy = pointer.y - event.clientY;
      pointer = null;
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) go(index + (dx > 0 ? 1 : -1));
    });
    on(viewport, 'pointercancel', () => { pointer = null; });
    slides.forEach((slide, i) => {
      slide.querySelectorAll('video').forEach(video => on(video, 'play', () => {
        if (i !== index || document.hidden) video.pause();
      }));
    });
    on(document, 'visibilitychange', () => {
      if (document.hidden) slides.forEach(pauseSlide);
      else loadExternal(slides[index]);
    });
    lifecycle.signal.addEventListener('abort', () => {
      pointer = null;
      slides.forEach(pauseSlide);
    }, { once: true });
    go(0);
  }

  // =========================================================
  // INIT PRODUCT PAGE
  // =========================================================
  function findReviewWidget(root) {
    const id = root.querySelector('[data-review-product-id]')?.dataset.reviewProductId;
    return Array.from(document.querySelectorAll('#judgeme_product_reviews, .jdgm-review-widget')).find((widget) => {
      const widgetId = widget.dataset.productId || widget.dataset.id;
      return (!widgetId || widgetId === id) && widget.getClientRects().length;
    });
  }

  function initJudgeMeSummary(root, signal) {
    const summaries = root.querySelectorAll('[data-carisma-review-summary]');
    if (!summaries.length) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      const id = summaries[0].dataset.reviewProductId;
      const widget = findReviewWidget(root);
      const badge = Array.from(document.querySelectorAll('.jdgm-preview-badge')).find(el => el.dataset.id === id);
      const source = widget?.querySelector('[data-average-rating][data-number-of-reviews]') || badge?.querySelector('[data-average-rating][data-number-of-reviews]');
      const data = window.jdgm?.data?.reviewWidget?.[id];
      if (!source && !data) return;
      const count = Number(source ? source.dataset.numberOfReviews : data.number_of_reviews);
      const average = Number(source ? source.dataset.averageRating : data.average_rating);
      if (!Number.isFinite(count) || !Number.isFinite(average) || count < 0) return;
      summaries.forEach(summary => {
        const ratingText = count ? `${Math.round(average * 10) / 10}/5` : 'Reviews';
        const countText = `${count} ${count === 1 ? 'review' : 'reviews'}`;
        const rating = summary.querySelector('[data-review-average]');
        const total = summary.querySelector('[data-review-count]');
        if (rating.textContent !== ratingText) rating.textContent = ratingText;
        if (total.textContent !== countText) total.textContent = countText;
      });
    };
    const observer = new MutationObserver(records => {
      if (records.every(record => Array.from(summaries).some(summary => summary.contains(record.target)))) return;
      if (!frame) frame = requestAnimationFrame(sync);
    });
    observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-average-rating', 'data-number-of-reviews']});
    sync();
    signal.addEventListener('abort', () => { observer.disconnect(); cancelAnimationFrame(frame); }, {once: true});
  }

  function initProductPage(root) {
    if (root.dataset.carismaInitialized === 'true') return;
    root.dataset.carismaInitialized = 'true';
    const lifecycle = new AbortController();
    const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, {...options, signal:lifecycle.signal});
    root.carismaCleanup = () => { lifecycle.abort(); delete root.dataset.carismaInitialized; };
    initProductGallery(root, lifecycle);
    initJudgeMeSummary(root, lifecycle.signal);

    // Keep FAQ button behavior scoped to its own list, including editor renders.
    listen(root,'click', (event) => {
      const button = event.target.closest('.carisma-faq-design button[data-faq-toggle]');
      if (!button || !root.contains(button)) return;
      const list = button.closest('.carisma-faq-list');
      if (!list) return;
      const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

      list.querySelectorAll('button[data-faq-toggle]').forEach((toggle) => {
        const answer = toggle.closest('.carisma-faq-item')?.querySelector('[data-faq-answer]');
        if (!answer) return;
        const open = toggle === button && shouldOpen;
        toggle.setAttribute('aria-expanded', String(open));
        answer.hidden = !open;
      });
    }, { signal: lifecycle.signal });

    const packCards = root.querySelectorAll('[data-pack-card]');

    const selectedVariantInput = root.querySelector(
      '[data-selected-variant-id]'
    );

    const reviewPack = root.querySelector('[data-review-pack]');
    const reviewPrice = root.querySelector('[data-review-price]');
    const reviewShipping = root.querySelector('[data-review-shipping]');

    const tapCollectBtn = root.querySelector('[data-tap-collect]');
    const reviewPanel = root.querySelector('[data-review-panel]');

    const form = root.querySelector('.carisma-buy-form');

    const addToCartBtn = root.querySelector(
      '[data-add-to-cart-button]'
    );

    const cartStatus = root.querySelector('[data-cart-status]');
    const packMessage = root.querySelector('[data-pack-message]');

    let cartPending = false;
    let lastShippingText = 'Calculated by pincode above';

    // =========================================================
    // PACK MESSAGE
    // =========================================================
    function updatePackMessage(card) {
      if (!packMessage) return;

      const titleElement = card.querySelector(
        '.carisma-pack-card__title'
      );

      const priceElement = card.querySelector(
        '.carisma-pack-card__price'
      );

      const title = titleElement
        ? titleElement.textContent.trim()
        : '';

      const price = priceElement
        ? priceElement.textContent.trim()
        : '';

      const available =
        card.getAttribute('data-available') === 'true';

      if (!available) {
        packMessage.textContent =
          title + ' pack selected — currently sold out.';

        packMessage.dataset.status = 'fail';

        return;
      }

      packMessage.dataset.status = '';

      packMessage.textContent =
        title + ' pack selected · ' + price + '.';
    }

    // =========================================================
    // SELECT VARIANT / PACK
    // =========================================================
    function selectPack(card) {
      packCards.forEach((c) => {
        c.classList.remove('is-selected');

        c.setAttribute(
          'aria-pressed',
          'false'
        );
      });

      card.classList.add('is-selected');

      card.setAttribute(
        'aria-pressed',
        'true'
      );

      const variantId =
        card.getAttribute('data-variant-id');

      if (selectedVariantInput) {
        selectedVariantInput.value = variantId;
      }

      const title = card.querySelector(
        '.carisma-pack-card__title'
      );

      const price = card.querySelector(
        '.carisma-pack-card__price'
      );

      if (reviewPack && title) {
        reviewPack.textContent =
          title.textContent.trim();
      }

      if (reviewPrice && price) {
        reviewPrice.textContent =
          price.textContent.trim();
      }

      if (reviewShipping) {
        reviewShipping.textContent =
          lastShippingText;
      }

      if (addToCartBtn) {
        const available =
          card.getAttribute('data-available') === 'true';

        addToCartBtn.disabled =
          !available || cartPending;

        addToCartBtn.textContent =
          cartPending ? 'Adding...' : available
            ? 'Add to Cart'
            : 'Sold Out';
      }

      updatePackMessage(card);
    }

    packCards.forEach((card) => {
      listen(card,
        'click',
        () => { if (!cartPending) selectPack(card); }
      );
    });

    const firstAvailable =
      Array.from(packCards).find(
        (card) =>
          card.getAttribute('data-available') === 'true'
      ) || packCards[0];

    if (firstAvailable) {
      selectPack(Array.from(packCards).find(card => card.dataset.variantId === (new URL(location.href).searchParams.get('variant') || selectedVariantInput?.value)) || firstAvailable);
    }

    // =========================================================
    // QUICK LINKS
    // Reviews / Test Reports / FAQs
    // =========================================================
    root.querySelectorAll('[data-scroll-to]').forEach((btn) => {
      listen(btn,'click', () => {
        const targetId =
          btn.getAttribute('data-scroll-to');

        const target = btn.hasAttribute('data-carisma-review-link')
          ? findReviewWidget(root)
          : document.getElementById(targetId);

        if (target) {
          let offset = 16;
          document.querySelectorAll('header, sticky-header, .section-header, .shopify-section-header, [data-carbwell-header]').forEach((header) => {
            const style = getComputedStyle(header);
            const rect = header.getBoundingClientRect();
            if (['sticky', 'fixed'].includes(style.position) && rect.bottom > 0) offset = Math.max(offset, rect.height + 16);
          });
          window.scrollTo({top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});

          target.focus({
            preventScroll: true
          });
        }
      });
    });

    // =========================================================
    // PRODUCT DESCRIPTION
    // Dynamic Shopify description
    // 3 / 4 / 5 line preview
    // Inline Read More / Read Less
    // =========================================================
    const descriptionWrapper =
      root.querySelector(
        '[data-product-description]'
      );

    const descriptionBody =
      root.querySelector(
        '[data-description-body]'
      );

    const descriptionTemplate =
      root.querySelector(
        '[data-description-template]'
      );

    if (
      descriptionWrapper &&
      descriptionBody &&
      descriptionTemplate
    ) {
      const fullHTML =
        descriptionTemplate.innerHTML.trim();

      const temp =
        document.createElement('div');

      temp.innerHTML = fullHTML;

      // Plain text used only for collapsed preview.
      const fullText = (
        temp.textContent || ''
      )
        .replace(/\s+/g, ' ')
        .trim();

      /*
       * Preview lines come from Liquid:
       *
       * data-preview-lines="3"
       *
       * Allowed:
       * 3, 4 or 5
       */
      const configuredLines = parseInt(
        descriptionWrapper.getAttribute(
          'data-preview-lines'
        ) || '3',
        10
      );

      const previewLines =
        Number.isFinite(configuredLines)
          ? Math.max(
              3,
              Math.min(
                5,
                configuredLines
              )
            )
          : 3;

      let expanded = false;
      let resizeTimer;

      // =====================================================
      // CREATE READ MORE / READ LESS BUTTON
      // =====================================================
      function createActionButton(
        label,
        shouldCollapse
      ) {
        const button =
          document.createElement('button');

        button.type = 'button';

        button.className =
          'carisma-description-action';

        button.textContent = label;

        button.setAttribute(
          'aria-expanded',
          shouldCollapse
            ? 'true'
            : 'false'
        );

        listen(button,
          'click',
          () => {
            if (shouldCollapse) {
              showPreview(true);
            } else {
              showFull();
            }
          }
        );

        return button;
      }

      // =====================================================
      // TEMPORARY PREVIEW RENDER
      // Used to calculate whether text + Read More fits
      // =====================================================
      function renderPreviewCandidate(text) {
        descriptionBody.innerHTML = '';

        const preview =
          document.createElement('span');

        preview.className =
          'carisma-description-preview';

        preview.textContent = text;

        descriptionBody.appendChild(
          preview
        );

        descriptionBody.appendChild(
          document.createTextNode(' ')
        );

        descriptionBody.appendChild(
          createActionButton(
            'Read More',
            false
          )
        );
      }

      // =====================================================
      // CHECK WHETHER TEXT FITS WITHIN PREVIEW LINES
      // =====================================================
      function candidateFits(text) {
        renderPreviewCandidate(text);

        const style =
          window.getComputedStyle(
            descriptionBody
          );

        let lineHeight =
          parseFloat(
            style.lineHeight
          );

        if (
          !Number.isFinite(lineHeight)
        ) {
          const fontSize =
            parseFloat(
              style.fontSize
            ) || 16;

          lineHeight =
            fontSize * 1.7;
        }

        const maxHeight =
          lineHeight *
            previewLines +
          2;

        return (
          descriptionBody.scrollHeight <=
          maxHeight
        );
      }

      // =====================================================
      // FIND MAXIMUM TEXT WHICH FITS
      // Read More stays on the last line
      // =====================================================
      function getPreviewText() {
        if (!fullText) {
          return '';
        }

        // Full text already fits in preview area.
        if (candidateFits(fullText)) {
          return fullText;
        }

        let low = 0;
        let high = fullText.length;
        let best = '';

        /*
         * Binary search is used so this stays fast
         * even for long Shopify descriptions.
         */
        while (low <= high) {
          const middle =
            Math.floor(
              (low + high) / 2
            );

          let candidate =
            fullText
              .slice(
                0,
                middle
              )
              .trim();

          // Don't break a word in half.
          if (
            middle <
            fullText.length
          ) {
            const lastSpace =
              candidate.lastIndexOf(' ');

            if (
              lastSpace >
              0
            ) {
              candidate =
                candidate
                  .slice(
                    0,
                    lastSpace
                  )
                  .trim();
            }
          }

          if (
            candidate &&
            candidateFits(
              candidate
            )
          ) {
            best =
              candidate;

            low =
              middle + 1;
          } else {
            high =
              middle - 1;
          }
        }

        /*
         * Safety fallback.
         */
        return (
          best ||
          fullText
            .split(' ')
            .slice(0, 8)
            .join(' ')
        );
      }

      // =====================================================
      // COLLAPSED PREVIEW
      // =====================================================
      function showPreview(scrollBack) {
        expanded = false;

        descriptionBody.innerHTML = '';

        if (!fullText) {
          return;
        }

        const previewText =
          getPreviewText();

        /*
         * getPreviewText temporarily renders elements
         * while measuring, so clean it again.
         */
        descriptionBody.innerHTML = '';

        const preview =
          document.createElement('span');

        preview.className =
          'carisma-description-preview';

        preview.textContent =
          previewText;

        descriptionBody.appendChild(
          preview
        );

        /*
         * Only show Read More when description
         * actually has hidden content.
         */
        if (
          previewText.length <
          fullText.length
        ) {
          descriptionBody.appendChild(
            document.createTextNode(' ')
          );

          descriptionBody.appendChild(
            createActionButton(
              'Read More',
              false
            )
          );
        }

        /*
         * When Read Less is clicked and the user
         * is lower on the page, bring Product Summary
         * back into view.
         */
        if (scrollBack) {
          const rect =
            descriptionWrapper.getBoundingClientRect();

          if (rect.top < 0) {
            descriptionWrapper.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }

      // =====================================================
      // FULL DESCRIPTION
      // =====================================================
      function showFull() {
        expanded = true;

        descriptionBody.innerHTML = '';

        const full =
          document.createElement('div');

        full.className =
          'carisma-description-full';

        /*
         * IMPORTANT:
         * Original Shopify Product Description HTML
         * is restored here.
         *
         * Paragraphs
         * Bold
         * Links
         * Lists
         * etc remain intact.
         */
        full.innerHTML =
          fullHTML;

        descriptionBody.appendChild(
          full
        );

        const readLess =
          createActionButton(
            'Read Less',
            true
          );

        /*
         * Add Read Less after the last visible
         * content block so it stays inline where
         * possible.
         */
        const blocks =
          full.querySelectorAll(
            'p, li, div'
          );

        const lastBlock =
          blocks.length
            ? blocks[
                blocks.length - 1
              ]
            : full;

        lastBlock.appendChild(
          document.createTextNode(' ')
        );

        lastBlock.appendChild(
          readLess
        );
      }

      // Initial render
      requestAnimationFrame(() => {
        if (root.isConnected) showPreview(false);
      });

      // Recalculate preview on responsive resize.
      listen(window,
        'resize',
        () => {
          clearTimeout(
            resizeTimer
          );

          resizeTimer =
            setTimeout(
              () => {
                if (!expanded && root.isConnected) {
                  showPreview(
                    false
                  );
                }
              },
              150
            );
        },
        { signal: lifecycle.signal }
      );
    }

    // =========================================================
    // TAP TO COLLECT
    // =========================================================
    if (
      tapCollectBtn &&
      reviewPanel
    ) {
      tapCollectBtn.setAttribute(
        'aria-expanded',
        'false'
      );

      listen(tapCollectBtn,
        'click',
        () => {
          const isOpen =
            tapCollectBtn.getAttribute(
              'aria-expanded'
            ) === 'true';

          tapCollectBtn.setAttribute(
            'aria-expanded',
            String(!isOpen)
          );

          reviewPanel.hidden =
            isOpen;
        }
      );
    }

    // =========================================================
    // SHIPPING / PINCODE
    // =========================================================
    const pincodeInput =
      root.querySelector(
        '[data-pincode-input]'
      );

    const pincodeSubmit =
      root.querySelector(
        '[data-pincode-submit]'
      );

    const shippingBox =
      root.querySelector(
        '[data-shipping-country]'
      );

    const shippingCountry =
      (
        shippingBox &&
        shippingBox.getAttribute(
          'data-shipping-country'
        )
      ) ||
      'India';

    const pinResult =
      root.querySelector(
        '[data-pin-result]'
      );

    const pinResultIcon =
      root.querySelector(
        '[data-pin-result-icon]'
      );

    const pinResultText =
      root.querySelector(
        '[data-pin-result-text]'
      );

    const pinResultShipping =
      root.querySelector(
        '[data-pin-result-shipping]'
      );

    const pinResultPrice =
      root.querySelector(
        '[data-pin-result-price]'
      );

    function checkPincode() {
      if (
        !pincodeInput ||
        !pinResult
      ) {
        return;
      }

      const value =
        pincodeInput.value.trim();

      if (
        !/^\d{4,6}$/.test(value)
      ) {
        pinResult.hidden =
          true;

        if (packMessage) {
          packMessage.dataset.status =
            'fail';

          packMessage.textContent =
            'Please enter a valid pincode.';
        }

        return;
      }

      pinResult.hidden =
        true;

      if (packMessage) {
        packMessage.textContent =
          '';
      }

      const params =
        new URLSearchParams({
          'shipping_address[zip]':
            value,

          'shipping_address[country]':
            shippingCountry
        });

      fetch(
        '/cart/shipping_rates.json?' +
          params.toString(),
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              'rate-lookup-failed'
            );
          }

          return res.json();
        })

        .then((data) => {
          const rates =
            (
              data &&
              data.shipping_rates
            ) ||
            [];

          pinResult.hidden =
            false;

          // No delivery available
          if (
            rates.length === 0
          ) {
            pinResult.dataset.status =
              'fail';

            if (pinResultIcon) {
              pinResultIcon.textContent =
                '✕';
            }

            if (pinResultText) {
              pinResultText.textContent =
                'Not deliverable to ' +
                value +
                '.';
            }

            if (
              pinResultShipping
            ) {
              pinResultShipping.hidden =
                true;
            }

            lastShippingText =
              'Not available for this pincode';
          }

          // Delivery available
          else {
            const cheapest =
              rates.reduce(
                (a, b) =>
                  parseFloat(
                    a.price
                  ) <=
                  parseFloat(
                    b.price
                  )
                    ? a
                    : b
              );

            const priceFormatted =
              money(
                Math.round(
                  parseFloat(
                    cheapest.price
                  ) * 100
                ), root.dataset.currency || window.Shopify?.currency?.active
              );

            pinResult.dataset.status =
              'ok';

            if (pinResultIcon) {
              pinResultIcon.textContent =
                '✓';
            }

            if (pinResultText) {
              pinResultText.textContent =
                'Delivery available at ' +
                value;
            }

            if (
              pinResultShipping
            ) {
              pinResultShipping.hidden =
                false;
            }

            if (pinResultPrice) {
              pinResultPrice.textContent =
                priceFormatted;
            }

            lastShippingText =
              priceFormatted +
              ' (' +
              cheapest.name +
              ')';
          }

          if (reviewShipping) {
            reviewShipping.textContent =
              lastShippingText;
          }
        })

        .catch(() => {
          pinResult.hidden =
            true;

          if (packMessage) {
            packMessage.dataset.status =
              'fail';

            packMessage.textContent =
              'Could not check shipping right now.';
          }
        });
    }

    if (pincodeSubmit) {
      listen(pincodeSubmit,
        'click',
        checkPincode
      );
    }

    if (pincodeInput) {
      listen(pincodeInput,
        'keydown',
        (e) => {
          if (
            e.key === 'Enter'
          ) {
            e.preventDefault();

            checkPincode();
          }
        }
      );
    }

    // =========================================================
    // ADD TO CART AJAX
    // =========================================================
    if (form) {
      listen(form,
        'submit',
        function (e) {
          e.preventDefault();

          if (
            cartPending || !addToCartBtn ||
            addToCartBtn.disabled
          ) {
            return;
          }

          cartPending = true;
          addToCartBtn.disabled =
            true;

          const originalText =
            addToCartBtn.textContent;

          addToCartBtn.textContent =
            'Adding...';

          const formData = new FormData(form);
          const dawnCart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          const sections = dawnCart?.getSectionsToRender?.().map(section => section.id) || [];
          formData.set('sections', [...new Set([...sections, 'cart-icon-bubble'])].join(','));
          formData.set('sections_url', window.location.pathname);
          dawnCart?.setActiveElement?.(document.activeElement);

          const cartUrl =
            window.Shopify &&
            window.Shopify.routes &&
            window.Shopify.routes.root
              ? window.Shopify.routes.root +
                'cart/add.js'
              : '/cart/add.js';

          fetch(
            cartUrl,
            {
              method: 'POST',

              headers: {
                Accept:
                  'application/json'
              },

              body:
                formData
            }
          )
            .then(
              (res) =>
                res.json()
            )

            .then(async (data) => {
              if (data.status) {
                throw new Error(
                  data.description ||
                    'Could not add to cart'
                );
              }

              if (cartStatus) {
                cartStatus.hidden =
                  false;

                cartStatus.style.color =
                  '';

                cartStatus.textContent =
                  'Added to cart!';
              }

              document.dispatchEvent(
                new CustomEvent(
                  'cart:updated',
                  {
                    detail:
                      data
                  }
                )
              );

              // Refresh Dawn's count and contents from the same successful add response.
              const bubbleHTML = data.sections?.['cart-icon-bubble'];
              const bubble = document.querySelector('#cart-icon-bubble');
              if (bubbleHTML && bubble) {
                const parsed = new DOMParser().parseFromString(bubbleHTML, 'text/html');
                const content = parsed.querySelector('#cart-icon-bubble') || parsed.querySelector('.shopify-section');
                if (content) bubble.innerHTML = content.innerHTML;
                else updateCartIconBubble();
              } else updateCartIconBubble();
              let cartOpened = false;
              if (dawnCart?.renderContents) {
                try {
                  dawnCart.classList.remove('is-empty');
                  await dawnCart.renderContents(data);
                  cartOpened = true;
                } catch (error) { console.warn('Carisma cart display refresh failed', error); }
              }
              if (!cartOpened) {
                let notice = root.querySelector('[data-cart-toast]');
                if (!notice) {
                  notice = document.createElement('div');
                  notice.className = 'carisma-cart-toast';
                  notice.dataset.cartToast = '';
                  notice.setAttribute('role', 'status');
                  const message = document.createElement('span');
                  message.textContent = 'Added to cart! ';
                  const link = document.createElement('a');
                  link.href = (window.Shopify?.routes?.root || '/') + 'cart';
                  link.textContent = 'View cart';
                  const close = document.createElement('button');
                  close.type = 'button'; close.textContent = '×';
                  close.setAttribute('aria-label', 'Dismiss notification');
                  listen(close,'click', () => { notice.hidden = true; }, {signal:lifecycle.signal});
                  notice.append(message, link, close);
                  root.append(notice);
                }
                notice.hidden = false;
              }
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, {source:'carisma-product', productVariantId:formData.get('id'), cartData:data});
              }
            })

            .catch((err) => {
              if (cartStatus) {
                cartStatus.hidden =
                  false;

                cartStatus.style.color =
                  '#b3261e';

                cartStatus.textContent =
                  err.message ||
                  'Something went wrong. Please try again.';
              }
            })

            .finally(() => {
              cartPending = false;
              const current = root.querySelector('[data-pack-card][aria-pressed="true"]');
              const available = current ? current.dataset.available === 'true' : true;
              addToCartBtn.disabled = !available;
              addToCartBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
            });
        }
      );
    }
    // Mirror the existing cart form; keep a single AJAX submission path.
    const mobileBar = root.querySelector('[data-mobile-cart]');
    if (mobileBar && form && addToCartBtn) {
      const mobileHost = document.createElement('div');
      mobileHost.className = 'carisma-product-page carisma-mobile-cart-portal';
      mobileHost.style.display = 'contents';
      const sourceStyle = getComputedStyle(root);
      for (const property of ['--c-green','--c-muted','--font-body-family']) mobileHost.style.setProperty(property, sourceStyle.getPropertyValue(property));
      mobileHost.append(mobileBar);document.body.append(mobileHost);
      const mobileButton = mobileBar.querySelector('[data-mobile-add]');
      const media = window.matchMedia('(max-width: 991px)');
      const sync = () => {
        const visible = media.matches && window.scrollY >= 100;
        mobileBar.hidden = !media.matches;
        mobileBar.classList.toggle('is-visible', visible);
        mobileBar.inert = !visible;
        mobileBar.setAttribute('aria-hidden', String(!visible));
        root.classList.toggle('carisma-has-mobile-cart', visible);
        mobileButton.disabled = cartPending || addToCartBtn.disabled;
        mobileButton.textContent = addToCartBtn.textContent;
        mobileBar.querySelector('[data-mobile-price]').textContent = reviewPrice?.textContent || '';
        mobileBar.querySelector('[data-mobile-pack]').textContent = reviewPack?.textContent || '';
        const message = cartStatus && !cartStatus.hidden ? cartStatus.textContent : '';
        const status = mobileBar.querySelector('[data-mobile-status]');
        if (status.textContent !== message) status.textContent = message;
      };
      listen(mobileButton,'click', () => {
        if (!cartPending && !addToCartBtn.disabled) form.requestSubmit(addToCartBtn);
      }, { signal: lifecycle.signal });
      listen(window,'scroll', sync, { passive: true, signal: lifecycle.signal });
      listen(window,'resize', sync, { passive: true, signal: lifecycle.signal });
      const observer = new MutationObserver(sync);
      [addToCartBtn, reviewPrice, reviewPack, cartStatus].filter(Boolean).forEach(el => observer.observe(el, {attributes:true, childList:true, subtree:true, characterData:true}));
      lifecycle.signal.addEventListener('abort', () => { observer.disconnect(); mobileBar.hidden = true; root.append(mobileBar);mobileHost.remove();root.classList.remove('carisma-has-mobile-cart'); });
      sync();
    }
  }
})();


